import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit3, Save, X, FileText, ExternalLink, MessageCircle } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import { buildPageShareMessage } from '../utils/shareMessages';
import AdminSearch from './AdminSearch';

const SEED_SLUGS = [
  { slug: 'about-us', title: 'About Us' },
  { slug: 'contact-us', title: 'Contact Us' },
  { slug: 'terms-and-conditions', title: 'Terms & Conditions' },
  { slug: 'privacy-policy', title: 'Privacy Policy' },
  { slug: 'careers', title: 'Careers' },
  { slug: 'refund-policy', title: 'Refund Policy' },
  { slug: 'feedback', title: 'Feedback' },
  { slug: 'send-enquiry', title: 'Send Enquiry' },
];

const PagesManager = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', title: '', body: '', published: true, seo_title: '', seo_description: '', hero_image: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pages`, { headers: adminAuthHeader() });
      // Merge with seeds so all default slugs are shown even if never edited
      const map = new Map();
      SEED_SLUGS.forEach((s) => map.set(s.slug, { ...s, body: '', published: true }));
      (res.data || []).forEach((p) => map.set(p.slug, p));
      setItems(Array.from(map.values()));
    } catch (_) { setItems(SEED_SLUGS.map((s) => ({ ...s, body: '', published: true }))); } finally { setLoading(false); }
  };

  const startEdit = (p) => {
    setEditing(p.slug);
    setForm({
      slug: p.slug,
      title: p.title || '',
      body: p.body || '',
      published: p.published !== false,
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || '',
      hero_image: p.hero_image || '',
    });
  };
  const cancel = () => { setEditing(null); setMsg(''); };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/admin/pages/${form.slug}`, form, { headers: adminAuthHeader() });
      setMsg('Saved!');
      await load();
      setTimeout(() => { setMsg(''); setEditing(null); }, 900);
    } catch (_) { setMsg('Save failed'); } finally { setSaving(false); }
  };

  const shareWA = (p) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/api/share/page/${p.slug}`;
    const text = buildPageShareMessage(p, url);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  if (editing) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Editing: /{form.slug}</h3>
          <div className="flex gap-2">
            <button onClick={cancel} className="text-sm text-gray-600 border border-gray-200 rounded-md px-3 py-1.5"><X size={13} className="inline mr-1"/> Cancel</button>
            <button onClick={save} disabled={saving} className="text-sm bg-[#0d64a8] text-white rounded-md px-4 py-1.5 disabled:opacity-70"><Save size={13} className="inline mr-1"/> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Page Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Body <span className="text-gray-400">(supports paragraphs — separate with blank lines)</span></label>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={16} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] font-mono leading-relaxed" placeholder="Write your page content here. Separate paragraphs with a blank line." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.published ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, published: e.target.value === 'yes' })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                <option value="yes">Published</option>
                <option value="no">Draft (hidden)</option>
              </select>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">SEO Meta</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SEO Title</label>
                <input value={form.seo_title || ''} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="Defaults to page title" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">SEO Description</label>
                <textarea value={form.seo_description || ''} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} rows={2} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Hero / Social Preview Image</label>
                <input
                  value={form.hero_image || ''}
                  onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                  placeholder="Paste an image URL or /api/uploads/... path — used as og:image on social shares"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
                  data-testid="page-hero-image-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">Shown when this landing page is shared on WhatsApp / social media.</p>
              </div>
            </div>
          </div>
          {msg && <div className="text-sm text-green-600">{msg}</div>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">Static Pages</h2>
        <p className="text-sm text-gray-500">Edit the pages linked from your footer (About, Contact, Terms, Privacy, etc.)</p>
      </div>
      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search title, slug..." testId="pages-search" />
      </div>
      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.filter((p) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            return [p.title, p.slug, p.body].filter(Boolean).join(' ').toLowerCase().includes(s);
          }).map((p) => (
            <div key={p.slug} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#0d64a8]/10 text-[#0d64a8] flex items-center justify-center shrink-0"><FileText size={16}/></div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{p.title}</h3>
                  <div className="text-[11px] text-gray-500 mt-0.5">/page/{p.slug} · {p.published === false ? 'DRAFT' : 'LIVE'}</div>
                  {p.body ? <p className="text-xs text-gray-600 mt-1 line-clamp-2">{p.body.substring(0, 140)}...</p> : <p className="text-xs text-gray-400 italic mt-1">Empty — add content</p>}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="View live page"><ExternalLink size={13}/></a>
                <button
                  onClick={() => shareWA(p)}
                  data-testid={`page-wa-share-${p.slug}`}
                  className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-[#25D366] hover:border-[#25D366] hover:bg-[#25D366]/10"
                  title="Share on WhatsApp"
                >
                  <MessageCircle size={13}/>
                </button>
                <button onClick={() => startEdit(p)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]" title="Edit"><Edit3 size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PagesManager;
